import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ProductSalesPage } from "@/components/pages/ProductSalesPage";
import { MARKETS, SITE } from "@/lib/catalog";
import { HREFLANG, LOCALES, parseLocale, type Locale } from "@/lib/i18n";
import { productQuery } from "@/lib/queries";

export const Route = createFileRoute("/producto/$slug")({
  validateSearch: (search: Record<string, unknown>): { lang?: Locale } => {
    const lang = parseLocale(search.lang);
    return lang === "es" ? {} : { lang };
  },
  loaderDeps: ({ search }) => ({ lang: search.lang ?? "es" }),
  loader: async ({ context, params, deps }) => {
    const product = await context.queryClient.ensureQueryData(
      productQuery(params.slug, deps.lang),
    );
    if (!product) throw notFound();
    return { product };
  },
  head: ({ params, loaderData, match }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Producto no disponible — Yustiz Haro Resources" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const locale = parseLocale((match.search as { lang?: string }).lang);
    const p = loaderData.product;
    const base = `https://${SITE.domain}/producto/${params.slug}`;
    const url = locale === "es" ? base : `${base}?lang=${locale}`;
    const title = p.metaTitle ?? `${p.name} — ${SITE.name}`;
    const description =
      p.metaDescription ?? p.shortDescription ?? `${p.name}, disponible en ${SITE.name}.`;
    const rawImage = p.ogImageUrl ?? p.coverUrl;
    // Las previews sociales exigen URL absoluta: completamos las rutas relativas.
    const image = rawImage
      ? rawImage.startsWith("http")
        ? rawImage
        : `https://${SITE.domain}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`
      : null;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:locale", content: locale === "pt" ? "pt_BR" : `${locale}_${locale === "en" ? "US" : "AR"}` },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...LOCALES.map((l) => ({
          rel: "alternate",
          hrefLang: HREFLANG[l],
          href: l === "es" ? base : `${base}?lang=${l}`,
        })),
        { rel: "alternate", hrefLang: "x-default", href: base },

        ...(p.coverUrl
          ? [
              {
                rel: "preload",
                as: "image",
                href: /^\/media\/.+\.jpe?g$/i.test(p.coverUrl)
                  ? p.coverUrl.replace(/\.jpe?g$/i, ".avif")
                  : p.coverUrl,
                fetchPriority: "high" as const,
              },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.name,
            sku: p.sku,
            description,
            ...(image ? { image: [image] } : {}),
            brand: { "@type": "Brand", name: SITE.name },
            additionalProperty: [
              ...(p.fileCount
                ? [
                    {
                      "@type": "PropertyValue",
                      name: "Cantidad de archivos",
                      value: String(p.fileCount),
                    },
                  ]
                : []),
              ...(p.fileCountNote
                ? [{ "@type": "PropertyValue", name: "Detalle de archivos", value: p.fileCountNote }]
                : []),
              ...(p.resolutions?.length
                ? [
                    {
                      "@type": "PropertyValue",
                      name: "Resoluciones",
                      value: p.resolutions.join(", "),
                    },
                  ]
                : []),
              ...(p.formats?.length
                ? [{ "@type": "PropertyValue", name: "Formatos", value: p.formats.join(", ") }]
                : []),
              ...(p.apps?.length
                ? [{ "@type": "PropertyValue", name: "Compatibilidad", value: p.apps.join(", ") }]
                : []),
            ],

            offers: (["INTL", "AR"] as const)
              .filter((m) => p.prices[m]?.amount)
              .map((m) => ({
                "@type": "Offer",
                priceCurrency: MARKETS[m].currency,
                price: p.prices[m].amount,
                availability: "https://schema.org/InStock",
                url,
              })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `https://${SITE.domain}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: "Recursos",
                item: `https://${SITE.domain}/recursos`,
              },
              { "@type": "ListItem", position: 3, name: p.name },
            ],
          }),
        },
        ...(p.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: p.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: ProductoNoEncontrado,
  pendingComponent: ProductSkeleton,
  pendingMs: 200,
  component: ProductRoute,
});

function ProductRoute() {
  const { slug } = Route.useParams();
  const { lang } = Route.useSearch();
  const locale = parseLocale(lang);
  const { data } = useSuspenseQuery(productQuery(slug, locale));
  if (!data) return <ProductoNoEncontrado />;
  return <ProductSalesPage product={data} locale={locale} />;
}


/** Esqueleto de producto: <main> nunca queda vacío ni salta el layout. */
function ProductSkeleton() {
  return (
    <div className="mx-auto max-w-7xl animate-pulse px-5 py-14 sm:px-8" aria-busy="true">
      <div className="h-3 w-48 rounded bg-surface-2" />
      <div className="mt-8 grid gap-10 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="h-3 w-28 rounded bg-surface-2" />
          <div className="h-10 w-full rounded bg-surface-2" />
          <div className="h-10 w-4/5 rounded bg-surface-2" />
          <div className="h-4 w-full rounded bg-surface-2" />
          <div className="h-4 w-3/4 rounded bg-surface-2" />
          <div className="h-12 w-56 rounded bg-surface-2" />
        </div>
        <div className="order-first aspect-video w-full rounded-xl bg-surface-2 lg:order-none lg:aspect-[5/4]" />
      </div>
    </div>
  );
}

function ProductoNoEncontrado() {
  return (
    <div className="mx-auto max-w-2xl px-5 py-24 sm:px-8">
      <p className="eyebrow">404</p>
      <h1 className="mt-3 font-display text-3xl uppercase">Este producto no está disponible</h1>
      <p className="mt-4 text-muted-foreground">
        Puede que haya cambiado de nombre o que ya no esté publicado.
      </p>
      <Link
        to="/recursos"
        className="mt-8 inline-flex rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
      >
        Ver el catálogo
      </Link>
    </div>
  );
}
